/**
 * Copyright (c) Omnissa, LLC. All rights reserved.
 * This product is protected by copyright and intellectual property laws in the
 * United States and other countries as well as by international treaties.
 * -- Omnissa Restricted
 *
 * @format
 */

import { Logger } from "@html-core";
import JSCDKBrokerError from "../../../shared/jscdk/model/jscdk-broker-error.consts";
import { SubError } from "../../../shared/jscdk/jscdk-interface";
import { trustedTypeUtil } from "../../../shared/jscdk/controllers/redirect/trusted-type-util";

enum ProxyFailReason {
   UNKNOWN = "UNKNOWN",
   ENV = "ENV",
   DEV = "DEV"
}

enum RequestFormat {
   UNKNOWN = "UNKNOWN",
   XML = "XML",
   REST = "REST"
}

enum RequestType {
   UNKNOWN = "UNKNOWN",
   Init = "Init",
   AJAX = "AJAX",
   Fetch = "Fetch",
   CancelLast = "CancelLastRequest",
   Icon = "Icon"
}

export class NetProxyService {
   private s_requestIdCounter = 0;
   private s_proxyPageWindowPromise = null;
   private s_proxyPageWindowOrigin = null;
   private s_lastMessageHandlingCB = null;
   private cachedInfo = {};

   constructor() {
      // @ts-ignore
      window.iwa = {
         sendRequestByProxy: this.sendRequestByProxy.bind(this)
      };
   }

   public disableProxyPage = () => {
      if (this.s_lastMessageHandlingCB) {
         window.removeEventListener("message", this.s_lastMessageHandlingCB);
         this.s_lastMessageHandlingCB = null;
      }
      this.s_proxyPageWindowPromise = null;
      Logger.info("clean up the proxy page");
   };

   public sendRequestByProxy = (
      brokerUrl,
      contextInfo,
      cacheThis,
      successCallback,
      async,
      timeout,
      skipNetworkError,
      sendFetch,
      dspec,
      csrfToken
   ) => {
      this._getProxyPage(brokerUrl, cacheThis, true)
         .then((proxyWindow) => {
            const origin = this.s_proxyPageWindowOrigin;
            const params = {
               type: RequestType.AJAX,
               requestId: this.s_requestIdCounter,
               request: contextInfo.messageText,
               url: brokerUrl,
               csrfToken: csrfToken,
               async: async,
               timeout: timeout
               //dspec: portalable dspecInfo
               //fetch: boolean, could always be false for IWA
            };
            this.cachedInfo[this.s_requestIdCounter] = {
               contextInfo: contextInfo,
               successCallback: successCallback,
               skipNetworkError: skipNetworkError
            };
            Logger.debug("sending XML request to IWA proxy page with id" + this.s_requestIdCounter);
            this.s_requestIdCounter++;
            proxyWindow.contentWindow.postMessage(params, origin);
         })
         .catch((e) => {
            this._handleProxyXMLFailure(e.errorType, e.errorDetail, cacheThis);
         });
      return null; //for abort
   };

   public getIconBlob = async (iconURL, blobSuccessCallback, errorCallback) => {
      const origin = this.s_proxyPageWindowOrigin;
      try {
         Logger.info("using proxy page to get icon for iWA" + iconURL);
         const proxyPage = await this._getProxyPage(iconURL);
         const params = {
            type: RequestType.Icon,
            requestId: this.s_requestIdCounter,
            url: iconURL
            //accessToken: portalable accessToken
         };

         this.cachedInfo[this.s_requestIdCounter] = {
            blobSuccessCallback: blobSuccessCallback,
            errorCallback: errorCallback
         };
         this.s_requestIdCounter++;
         proxyPage.contentWindow.postMessage(params, origin);
      } catch (e) {
         this._handleProxyIconFailure(e);
         errorCallback();
      }
   };

   private _getProxyPage = (requestUrl: string, cacheThis = null, allowCreate = false) => {
      const proxyPageOrigin = new URL(requestUrl).origin;
      const proxyPageURL = proxyPageOrigin + __PROXY_PATH__;
      this.s_proxyPageWindowOrigin = proxyPageOrigin;

      if (this.s_proxyPageWindowPromise && this.s_proxyPageWindowOrigin === proxyPageOrigin) {
         return this.s_proxyPageWindowPromise;
      } else if (allowCreate) {
         this.disableProxyPage();
         return (this.s_proxyPageWindowPromise = this._createProxyPage(proxyPageURL, cacheThis));
      } else {
         Logger.error("proxy page not found to load application icons for " + requestUrl);
         return Promise.reject(this._getProxyError(ProxyFailReason.DEV, "proxy page not found as loaded"));
      }
   };

   private _handleProxyXMLFailure = (errorType, errorDetail, cacheThis) => {
      let mockedNetworkError = {},
         subError = {} as SubError,
         msgError = {},
         errorObject;
      if (errorType === ProxyFailReason.ENV) {
         errorObject = JSCDKBrokerError.getError(JSCDKBrokerError.JSCDK_BROKER_ERROR_PROXY_PAGE_ERROR);
         subError.errorCode = errorObject.getErrorCode();
         subError.errorMessage = errorObject.getErrorMessage();
         subError.userMessage = errorObject.getErrorMessage();
         subError.skipNetworkError = false;
         subError.httpStatus = 404;
         subError.responseText = "proxy";
         // For Proxy init errors we can't skip network error.
      } else if (errorType === ProxyFailReason.DEV) {
         Logger.error("Found a fatal error in handleProxyXMLFailure:" + JSON.stringify(errorDetail));
      } else {
         Logger.error("Found a unknown error" + JSON.stringify(errorDetail));
      }
      msgError[cacheThis.name] = subError;
      mockedNetworkError["error"] = msgError;
      cacheThis.appendContent(mockedNetworkError);
      //push Error to user for mocked error
      cacheThis.pushErrorToUser(cacheThis);
   };

   private _getProxyError = (errorType, errorMessage) => {
      return { errorType, errorMessage };
   };

   private _onResponse = (response, initCallback, cacheThis) => {
      Logger.debug("get response from proxy page with type " + response.type);
      if (response.type === RequestType.Init) {
         this._handleProxyPageInit(response, initCallback);
      } else if (response.type === RequestType.AJAX) {
         this._handleProxyPageAJAX(response, cacheThis);
      } else if (response.type === RequestType.Icon) {
         this._handleProxyPageIcon(response);
      }
   };

   private _handleProxyPageInit = (response, initCallback) => {
      const initData = response.initData;
      const workingVersion = initData.workingVersion;
      const requestFormat = initData.requestFormat;
      Logger.info("the proxy page init with version of " + workingVersion + " and mode of " + requestFormat);
      if (requestFormat === RequestFormat.UNKNOWN) {
         Logger.error("the proxy page init failed, try to downgrade to work in compatible mode");
         // V1 need no downgrade, add codes here later if has more format supported other than XML and REST, also support customer to upgrade server to have it working
         return;
      }
      initCallback();
   };

   private _handleProxyPageAJAX = (response, cacheThis) => {
      const requestProxyId = response.requestId;
      Logger.debug("Get XML response from the IWA proxy page of id:" + requestProxyId);
      const requestCachedInfo = this.cachedInfo[requestProxyId];
      const contextInfo = requestCachedInfo.contextInfo;
      const successCallback = requestCachedInfo.successCallback;
      const skipNetworkError = requestCachedInfo.skipNetworkError;
      if (response.success) {
         const dataString = response.success.data;
         let trustedString;
         trustedString = trustedTypeUtil.createTrustedHTML(dataString);

         // safely use the trustedString with DOM APIs to check format and ensure the result used as XML
         const parser = new DOMParser();
         const xmlDoc = parser.parseFromString(trustedString, "text/xml"); // Use "text/html" for HTML

         const textStatus = response.success.textStatus;
         const jscdkXHR = null;
         successCallback(xmlDoc, textStatus, jscdkXHR);
      } else {
         const errorDetailFromProxy = response.error;
         const jscdkXHR = errorDetailFromProxy.jscdkXHR;
         const textStatus = errorDetailFromProxy.textStatus;
         const messageText = errorDetailFromProxy.messageText;
         cacheThis.jscdkPostError(contextInfo, jscdkXHR, textStatus, null, skipNetworkError);
      }
      delete this.cachedInfo[requestProxyId];
   };

   private _handleProxyPageIcon = (response) => {
      const requestProxyId = response.requestId;
      const requestCachedInfo = this.cachedInfo[requestProxyId];
      const blobSuccessCallback = requestCachedInfo.blobSuccessCallback;
      const errorCallback = requestCachedInfo.errorCallback;
      if (response.success) {
         let dataBlob = response.success.data;
         blobSuccessCallback(dataBlob);
      } else {
         errorCallback();
      }
      delete this.cachedInfo[requestProxyId];
   };

   private _createProxyPage = (proxyPageURL, cacheThis) => {
      if (this.s_proxyPageWindowPromise) {
         Logger.error("can't init the proxy page twice"); // This might need to be changed if supporting on-ramp on IWA.
         return;
      }
      Logger.info("initing proxy page for the XML exchange on IWA");

      return new Promise((resolve, reject) => {
         try {
            /**
             * Check here for exception handling when IWA connects to a server without proxy page hosted, like a old CS/UAG.
             * We need to mock a network error response to JSCDK layer to indicate user need to upgrade the server to have the IWA connectable to it.
             * Timer starts here, and after 2s, if the handshake between IWA and proxy page failed for any reason, shows the error.
             * Note: the cert error exception handling should already been handled before the first XML request, thus could be skipped here.
             **/
            const ProxyPageInitWaitTime = 10 * 1000;
            const proxyInitTimer = setTimeout(() => {
               Logger.error(
                  "Proxy page can't be used, thus IWA can't connect to the specified server, please check server compatible support or contact administrator for helps"
               );
               reject(this._getProxyError(ProxyFailReason.ENV, "timeout"));
            }, ProxyPageInitWaitTime);

            const controlledFrame: any = document.getElementById("proxy-frame");
            controlledFrame.oncontentload = () => {
               Logger.info("IWA Proxy page loaded");
               const messageHandlingCB = (e) => {
                  if (e.origin !== this.s_proxyPageWindowOrigin) {
                     Logger.trace(
                        "ignore the message from unknown origin of " +
                           e.origin +
                           ", while current origin is " +
                           this.s_proxyPageWindowOrigin
                     );
                     return;
                  }
                  try {
                     let data = e.data;
                     Logger.trace("get a valid proxy page message" + JSON.stringify(e.data));
                     this._onResponse(
                        data,
                        () => {
                           clearTimeout(proxyInitTimer);
                           resolve(controlledFrame);
                        },
                        cacheThis
                     );
                  } catch (error) {
                     Logger.error("Dev JS error" + JSON.stringify(error));
                  }
               };
               window.addEventListener("message", messageHandlingCB);
               this.s_lastMessageHandlingCB = messageHandlingCB;

               this._sendInitToProxy(controlledFrame);
            };
            Logger.debug("loading proxy page at " + proxyPageURL);
            controlledFrame.src = proxyPageURL;
         } catch (e) {
            Logger.error("load proxy page error" + JSON.stringify(e));
            reject(this._getProxyError(ProxyFailReason.DEV, "JS exception"));
         }
      });
   };

   private _sendInitToProxy = (controlledFrame) => {
      const origin = this.s_proxyPageWindowOrigin;
      const params = {
         type: RequestType.Init,
         requestId: undefined,
         initData: {
            version: 1,
            requestFormat: RequestFormat.XML
         }
      };
      controlledFrame.contentWindow.postMessage(params, origin);
   };

   private _handleProxyIconFailure = (error) => {
      Logger.error(error);
   };
}
